import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "../OnboardingShell";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import { APP_MAX_WIDTH } from "../../constants/layout";
import { FONTS, GLASS_SX } from "../../constants/styles";

const AdminInfo = () => {
    const navigate = useNavigate();

    const goToSignUp = () => {
        try {
            const brandRaw = JSON.parse(
                sessionStorage.getItem("brand") || "{}"
            );
            const brandId = brandRaw.brandId;
            const brandName = brandRaw.name;
            if (brandId) {
                navigate(
                    `/creation/signup?claim_brand=${brandId}&name=${encodeURIComponent(brandName || "")}`
                );
                return;
            }
        } catch (e) {
            console.error("Failed to read brand from sessionStorage:", e);
        }
        navigate("/creation/signup");
    };

    const goToClientApp = () => {
        const url =
            sessionStorage.getItem("clientPreviewUrl") ||
            sessionStorage.getItem("clientPreviewURL") ||
            "";
        if (!url) return;
        setTimeout(() => {
            window.location.href = url;
        }, 100);
    };

    return (
        <OnboardingShell variant="celebration">
            <Stack
                flex={1}
                width="100%"
                alignItems="center"
                justifyContent="center"
                spacing={4}
                sx={{
                    textAlign: "center",
                    px: 2,
                    boxSizing: "border-box",
                    pb: "calc(env(safe-area-inset-bottom) + 32px)",
                }}
            >
                <Stack
                    alignItems="center"
                    spacing={2}
                    sx={{
                        "@keyframes fadeUp": {
                            from: {
                                opacity: 0,
                                transform: "translateY(20px)",
                            },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeUp 0.4s ease-out",
                    }}
                >
                    {/* Icon with glass backdrop */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "24px",
                            ...GLASS_SX,
                            bgcolor: "rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <LockOpenRoundedIcon
                            sx={{ fontSize: 40, color: "white" }}
                        />
                    </Box>

                    <Stack spacing={1} alignItems="center">
                        <Typography
                            sx={{
                                fontFamily: FONTS.display,
                                fontWeight: 700,
                                fontSize: "24px",
                                color: "white",
                                lineHeight: 1.2,
                            }}
                        >
                            Unlock your Admin App
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: FONTS.body,
                                fontSize: "14px",
                                color: "rgba(255,255,255,0.75)",
                                maxWidth: 320,
                                lineHeight: 1.5,
                            }}
                        >
                            Create a free account to manage bookings, services,
                            and staff. It only takes a minute.
                        </Typography>
                    </Stack>
                </Stack>

                <Stack spacing={1.5} alignItems="center" width="100%">
                    <ActionCard
                        icon={
                            <PersonAddRoundedIcon
                                sx={{ color: "white", fontSize: 28 }}
                            />
                        }
                        title="Create an account"
                        description="Register for free and start managing"
                        onClick={goToSignUp}
                        primary
                        delay={100}
                    />
                    <ActionCard
                        icon={
                            <PhoneIphoneRoundedIcon
                                sx={{ color: "white", fontSize: 28 }}
                            />
                        }
                        title="Preview Client App first"
                        description="See what your customers will experience"
                        onClick={goToClientApp}
                        delay={200}
                    />
                </Stack>
            </Stack>
        </OnboardingShell>
    );
};

const ActionCard = ({
    icon,
    title,
    description,
    onClick,
    primary = false,
    delay = 0,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
    primary?: boolean;
    delay?: number;
}) => (
    <Box
        role="button"
        onClick={onClick}
        sx={{
            width: "90%",
            maxWidth: APP_MAX_WIDTH,
            cursor: "pointer",
            userSelect: "none",
            borderRadius: "20px",
            ...GLASS_SX,
            bgcolor: primary
                ? "rgba(255,255,255,0.22)"
                : "rgba(255,255,255,0.12)",
            p: 2,
            transition: "transform 0.15s ease, background 0.15s ease",
            "&:hover": {
                transform: "translateY(-2px)",
                bgcolor: "rgba(255,255,255,0.28)",
            },
            "@keyframes cardSlideUp": {
                from: { opacity: 0, transform: "translateY(16px)" },
                to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: `cardSlideUp 0.4s ease-out ${delay}ms both`,
        }}
    >
        <Stack direction="row" spacing={2} alignItems="center">
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    borderRadius: "16px",
                    bgcolor: "rgba(255,255,255,0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: "0 0 auto",
                }}
            >
                {icon}
            </Box>
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONTS.display,
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "white",
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: FONTS.body,
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.65)",
                        lineHeight: 1.3,
                    }}
                >
                    {description}
                </Typography>
            </Stack>
            <ChevronRightRoundedIcon
                sx={{
                    color: "rgba(255,255,255,0.5)",
                    flex: "0 0 auto",
                }}
            />
        </Stack>
    </Box>
);

export default AdminInfo;
